import { useState, useEffect, useCallback } from 'react';
import {
  History, Loader2, MessageSquare, PencilLine, RefreshCw, ShieldCheck,
  Link2, Plus, Check, UserCheck, AlertCircle, Archive, ArchiveRestore,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../foundation/altzero_base/base/components/ui/card';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { Label } from '../../../foundation/altzero_base/base/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../foundation/altzero_base/base/components/ui/select';
import { cn } from '../../../foundation/altzero_base/base/lib/utils';
import { auditService } from '../services/auditService';
import type { BvAuditEvent, BvAuditAction, BvAuditEntityType } from '../types/billValidationTypes';

type Props = {
  entityType: BvAuditEntityType;
  entityId: string;
  /** When set, the panel shows everything recorded against the site AND its
   *  bills/contracts/invoices rather than just the one entity. */
  rollUpSite?: boolean;
  siteId?: string | null;
  title?: string;
};

const ACTION_META: Record<BvAuditAction, { label: string; icon: React.ElementType; cls: string }> = {
  created:         { label: 'Created',          icon: Plus,          cls: 'bg-green-50 text-green-700 border-green-200' },
  updated:         { label: 'Updated',          icon: PencilLine,    cls: 'bg-gray-50 text-gray-600 border-gray-200' },
  deleted:         { label: 'Deleted',          icon: AlertCircle,   cls: 'bg-red-50 text-red-700 border-red-200' },
  note:            { label: 'Note',             icon: MessageSquare, cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  validation:      { label: 'Validation',       icon: ShieldCheck,   cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  manual_override: { label: 'Manual override',  icon: PencilLine,    cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  recalculation:   { label: 'Recalculated',     icon: RefreshCw,     cls: 'bg-purple-50 text-purple-700 border-purple-200' },
  assigned:        { label: 'Assigned',         icon: Link2,         cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  status_change:   { label: 'Status change',    icon: ShieldCheck,   cls: 'bg-teal-50 text-teal-700 border-teal-200' },
  archived:        { label: 'Archived',         icon: Archive,       cls: 'bg-gray-50 text-gray-600 border-gray-200' },
  restored:        { label: 'Restored',         icon: ArchiveRestore, cls: 'bg-green-50 text-green-700 border-green-200' },
};

function fmtValue(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'string') return v || '—';
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

/** "peak_kwh" → "Peak kwh" — field names come straight from the column. */
function fmtField(field: string): string {
  const spaced = field.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export const AuditHistoryPanel = ({ entityType, entityId, rollUpSite, siteId, title }: Props) => {
  const [events, setEvents] = useState<BvAuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [members, setMembers] = useState<Array<{ id: string; label: string }>>([]);

  // Note composer
  const [note, setNote] = useState('');
  const [assignee, setAssignee] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = rollUpSite && siteId
      ? await auditService.listForSite(siteId)
      : await auditService.list(entityType, entityId);
    if (res.success) {
      setEvents(res.data ?? []);
      setError(null);
    } else {
      setError(res.error ?? 'Failed to load history');
    }
    setLoading(false);
  }, [entityType, entityId, rollUpSite, siteId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let mounted = true;
    auditService.listOrgMembers().then(({ data }) => {
      if (mounted) setMembers(data ?? []);
    });
    return () => { mounted = false; };
  }, []);

  const handleAddNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    const res = await auditService.log({
      entity_type: entityType,
      entity_id:   entityId,
      site_id:     siteId ?? null,
      action:      'note',
      note:        note.trim(),
      assigned_to: assignee || null,
    });
    setSaving(false);
    if (!res.success) {
      setError(res.error ?? 'Failed to save note');
      return;
    }
    setNote('');
    setAssignee('');
    load();
  };

  const handleResolve = async (id: string) => {
    const res = await auditService.resolve(id);
    if (res.success) load();
    else setError(res.error ?? 'Failed to resolve');
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <History className="h-4 w-4 text-primary" />
          {title ?? 'History'}
          {events.length > 0 && (
            <span className="text-xs font-normal text-muted-foreground">({events.length})</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">

        {/* Note composer — an internal note is the one history entry a user
            writes by hand; everything else is recorded by the write paths. */}
        <div className="space-y-2 rounded-lg border border-gray-100 bg-gray-50/60 p-3">
          <Label className="text-xs text-gray-500">Add an internal note</Label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="What changed, and why?"
            className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-[200px]">
              <Select value={assignee} onValueChange={setAssignee}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder={members.length ? 'Assign follow-up (optional)' : 'No members to assign'} />
                </SelectTrigger>
                <SelectContent>
                  {members.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" disabled={!note.trim() || saving} onClick={handleAddNote}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Add note'}
            </Button>
          </div>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
        ) : events.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6 border border-dashed border-gray-200 rounded-lg">
            Nothing recorded yet. Edits, overrides and recalculations will appear here.
          </p>
        ) : (
          <ol className="space-y-2">
            {events.map((e) => {
              const meta = ACTION_META[e.action] ?? ACTION_META.updated;
              const open = e.assigned_to && !e.resolved_at;
              return (
                <li
                  key={e.id}
                  className={cn(
                    'rounded-lg border px-3 py-2.5 text-sm',
                    open ? 'border-amber-200 bg-amber-50/50' : 'border-gray-100 bg-white',
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium', meta.cls)}>
                          <meta.icon className="h-3 w-3" />
                          {meta.label}
                        </span>
                        {e.field && (
                          <span className="text-xs font-medium text-gray-700">{fmtField(e.field)}</span>
                        )}
                        {e.entity_type !== entityType && (
                          <span className="text-xs text-gray-400">on {e.entity_type}</span>
                        )}
                      </div>

                      {(e.old_value != null || e.new_value != null) && (
                        <p className="text-xs text-gray-600">
                          <span className="line-through text-gray-400">{fmtValue(e.old_value)}</span>
                          {' → '}
                          <span className="font-medium text-gray-900">{fmtValue(e.new_value)}</span>
                        </p>
                      )}

                      {e.note && <p className="text-sm text-gray-700 whitespace-pre-wrap">{e.note}</p>}

                      <p className="text-xs text-gray-400">
                        {e.actor_email ?? 'Unknown user'} · {new Date(e.created_at).toLocaleString('en-AU')}
                        {e.assignee && (
                          <>
                            {' · '}
                            <span className="inline-flex items-center gap-1 text-amber-700">
                              <UserCheck className="h-3 w-3" />
                              {e.resolved_at ? 'was assigned to' : 'assigned to'}{' '}
                              {e.assignee.full_name || e.assignee.email || 'a teammate'}
                            </span>
                          </>
                        )}
                        {e.resolved_at && ` · resolved ${new Date(e.resolved_at).toLocaleDateString('en-AU')}`}
                      </p>
                    </div>

                    {open && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="shrink-0 gap-1 text-xs"
                        onClick={() => handleResolve(e.id)}
                      >
                        <Check className="h-3.5 w-3.5" />
                        Resolve
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
};
